import type {
  Event,
  EventType,
  EventUser,
  EventStatus,
  EventContact,
  EventTag,
  CreateEventInput,
  UpdateEventInput,
  CreateEventUserInput,
  AddEventContactInput,
  UpdateEventContactInput,
  CreateEventTagInput,
  UpdateEventTagInput,
  EventVendorRecipient,
  SendVendorEmailInput,
  SendVendorEmailResult,
} from '@peles/shared';
import { apiClient } from './client';

export async function listEvents(filters?: {
  status?: EventStatus;
  eventTypeId?: string;
}): Promise<Event[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.eventTypeId) params.set('eventTypeId', filters.eventTypeId);

  const { data } = await apiClient.get<Event[]>('/events', { params });
  return data;
}

export async function getEvent(id: string): Promise<Event> {
  const { data } = await apiClient.get<Event>(`/events/${id}`);
  return data;
}

export async function createEvent(data: CreateEventInput): Promise<Event> {
  const { data: event } = await apiClient.post<Event>('/events', data);
  return event;
}

export async function updateEvent(id: string, updates: UpdateEventInput): Promise<Event> {
  const { data } = await apiClient.patch<Event>(`/events/${id}`, updates);
  return data;
}

export async function cancelEvent(id: string): Promise<Event> {
  const { data } = await apiClient.delete<Event>(`/events/${id}`);
  return data;
}

export async function listEventTypes(): Promise<EventType[]> {
  const { data } = await apiClient.get<EventType[]>('/event-types');
  return data;
}

export async function listEventUsers(eventId: string): Promise<EventUser[]> {
  const { data } = await apiClient.get<EventUser[]>(`/events/${eventId}/users`);
  return data;
}

export async function addEventUser(
  eventId: string,
  data: CreateEventUserInput,
): Promise<EventUser> {
  const { data: eventUser } = await apiClient.post<EventUser>(`/events/${eventId}/users`, data);
  return eventUser;
}

export async function removeEventUser(eventId: string, userId: string): Promise<void> {
  await apiClient.delete(`/events/${eventId}/users/${userId}`);
}

// ─── Event Contacts ─────────────────────────────────────────────────────────

export async function listEventContacts(eventId: string): Promise<EventContact[]> {
  const { data } = await apiClient.get<EventContact[]>(`/events/${eventId}/contacts`);
  return data;
}

export async function addEventContact(
  eventId: string,
  input: AddEventContactInput,
): Promise<EventContact> {
  const { data } = await apiClient.post<EventContact>(`/events/${eventId}/contacts`, input);
  return data;
}

export async function updateEventContact(
  eventId: string,
  contactId: string,
  updates: UpdateEventContactInput,
): Promise<EventContact> {
  const { data } = await apiClient.patch<EventContact>(
    `/events/${eventId}/contacts/${contactId}`,
    updates,
  );
  return data;
}

export async function removeEventContact(eventId: string, contactId: string): Promise<void> {
  await apiClient.delete(`/events/${eventId}/contacts/${contactId}`);
}

// ─── Event Logo ────────────────────────────────────────────────────────────

export async function getEventLogoUploadUrl(
  eventId: string,
  fileName: string,
  mimeType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const { data } = await apiClient.post<{ uploadUrl: string; s3Key: string }>(
    `/events/${eventId}/logo/upload-url`,
    { fileName, mimeType },
  );
  return data;
}

export async function setEventLogo(eventId: string, s3Key: string | null): Promise<Event> {
  const { data } = await apiClient.put<Event>(`/events/${eventId}/logo`, { s3Key });
  return data;
}

export async function deleteEventLogo(eventId: string): Promise<Event> {
  const { data } = await apiClient.delete<Event>(`/events/${eventId}/logo`);
  return data;
}

export async function getAppLogoUrl(): Promise<string | null> {
  const { data } = await apiClient.get<{ url: string | null }>(
    '/settings/app-settings/app-logo-url',
  );
  return data.url;
}

// ─── Event Tags ─────────────────────────────────────────────────────────────

export async function listEventTags(eventId: string): Promise<EventTag[]> {
  const { data } = await apiClient.get<EventTag[]>(`/events/${eventId}/tags`);
  return data;
}

export async function createEventTag(
  eventId: string,
  input: CreateEventTagInput,
): Promise<EventTag> {
  const { data } = await apiClient.post<EventTag>(`/events/${eventId}/tags`, input);
  return data;
}

export async function updateEventTag(
  eventId: string,
  tagId: string,
  updates: UpdateEventTagInput,
): Promise<EventTag> {
  const { data } = await apiClient.patch<EventTag>(`/events/${eventId}/tags/${tagId}`, updates);
  return data;
}

export async function deleteEventTag(eventId: string, tagId: string): Promise<void> {
  await apiClient.delete(`/events/${eventId}/tags/${tagId}`);
}

export async function sendVendorListEmail(
  eventId: string,
  input: { recipientEmails: string[]; replyTo?: string },
): Promise<void> {
  await apiClient.post(`/events/${eventId}/vendor-list-email`, input);
}

export async function getEventVendorRecipients(eventId: string): Promise<EventVendorRecipient[]> {
  const { data } = await apiClient.get<EventVendorRecipient[]>(
    `/events/${eventId}/vendors/recipients`,
  );
  return data;
}

export async function sendVendorEmail(
  eventId: string,
  input: SendVendorEmailInput,
): Promise<SendVendorEmailResult> {
  const { data } = await apiClient.post<SendVendorEmailResult>(
    `/events/${eventId}/vendors/send-email`,
    input,
  );
  return data;
}
